import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Play, Pause, SkipForward, SkipBack, Check, ChevronLeft,
  RotateCcw, Volume2, VolumeX, Music, Music2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HypeEngine, PRESETS, randomPhrase } from '@/lib/hypeMode';

/**
 * MEYBRA GUIDED TRAINING — Audio-only mode.
 *
 * Video recording was removed (Feb 2026) because uploaded session
 * footage is too large to be worth storing. Athletes who want to
 * record + share a clip use the Weekly Challenge flow instead.
 */
const GuidedTraining = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const sessionData = location.state?.session;
  const drills = sessionData?.drills || [];

  // Training state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);

  // Hype Mode state
  const [hypeVibe, setHypeVibe] = useState('HYPE'); // HYPE | FOCUS | PUMP | OFF
  const [hypeVolume, setHypeVolume] = useState(0.25);
  const hypeRef = useRef(null);
  if (!hypeRef.current) hypeRef.current = new HypeEngine();

  // Timer state
  const [currentSet, setCurrentSet] = useState(1);
  const [phase, setPhase] = useState('ready');
  const [timeRemaining, setTimeRemaining] = useState(0);

  const timerRef = useRef(null);
  const wakeLockRef = useRef(null);
  const midWorkPhraseFiredRef = useRef(false);
  const finalStretchFiredRef = useRef(false);

  const currentDrill = drills[currentIndex];
  const totalDrills = drills.length;
  const progress = totalDrills > 0 ? ((currentIndex + 1) / totalDrills) * 100 : 0;

  // Wake Lock — keep screen on during training
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
      }
    } catch (err) {
      // wake lock not supported / denied — silently ignore
    }
  };

  const releaseWakeLock = () => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release();
      wakeLockRef.current = null;
    }
  };

  useEffect(() => {
    if (isRunning && !isComplete) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isRunning && !isComplete) {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      releaseWakeLock();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isRunning, isComplete]);

  const getTimerConfig = useCallback((drill) => {
    if (!drill) return { type: 'Continuous', totalSeconds: 180 };
    const timerType = drill.timer_type || 'Continuous';
    if (timerType === 'Interval') {
      return {
        type: 'Interval',
        sets: drill.sets || 3,
        workSeconds: drill.work_seconds || 30,
        restSeconds: drill.rest_seconds || 15,
        restBetweenSets: drill.rest_between_sets_seconds || 0,
      };
    }
    return {
      type: 'Continuous',
      totalSeconds: (drill.total_minutes || drill.home_duration_minutes || 3) * 60,
    };
  }, []);

  // Shared AudioContext for beeps + keep-alive ping for speechSynthesis
  const beepCtxRef = useRef(null);
  const speechKeepAliveRef = useRef(null);
  const englishVoiceRef = useRef(null);

  // Pick a stable English voice. Mobile browsers may load voices async, so we
  // re-check on the `voiceschanged` event.
  const pickEnglishVoice = useCallback(() => {
    try {
      const voices = window.speechSynthesis.getVoices() || [];
      if (!voices.length) return null;
      // Preference order: en-US → en-GB → en-* → any voice flagged "english"
      const enUS = voices.find((v) => v.lang === 'en-US');
      if (enUS) return enUS;
      const enGB = voices.find((v) => v.lang === 'en-GB');
      if (enGB) return enGB;
      const enAny = voices.find((v) => (v.lang || '').toLowerCase().startsWith('en'));
      if (enAny) return enAny;
      const englishNamed = voices.find((v) => /english/i.test(v.name || ''));
      if (englishNamed) return englishNamed;
      return null;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    englishVoiceRef.current = pickEnglishVoice();
    const handler = () => {
      englishVoiceRef.current = pickEnglishVoice();
    };
    try {
      window.speechSynthesis.addEventListener('voiceschanged', handler);
    } catch (e) {}
    return () => {
      try {
        window.speechSynthesis.removeEventListener('voiceschanged', handler);
      } catch (e) {}
    };
  }, [pickEnglishVoice]);

  const getBeepCtx = useCallback(() => {
    if (!beepCtxRef.current) {
      beepCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (beepCtxRef.current.state === 'suspended') {
      beepCtxRef.current.resume().catch(() => {});
    }
    return beepCtxRef.current;
  }, []);

  // Voice — DOES NOT cancel prior speech (let utterances queue so coach
  // phrases + countdown all play). Urgent=true overrides (countdown).
  const speak = useCallback((text, urgent = false) => {
    if (!voiceEnabled || !text) return;
    try {
      const synth = window.speechSynthesis;
      if (urgent) synth.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.05;
      u.volume = 1;
      u.lang = 'en-US'; // hard-pin language so digits/numbers stay English
      const voice = englishVoiceRef.current || pickEnglishVoice();
      if (voice) {
        u.voice = voice;
        englishVoiceRef.current = voice; // cache for subsequent calls
      }
      synth.speak(u);
    } catch (e) {}
  }, [voiceEnabled, pickEnglishVoice]);

  // Test coach voice (used by preview "Test" button)
  const testCoach = useCallback(() => {
    try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) {}
    const u = new SpeechSynthesisUtterance("Let's go! Coach is on.");
    u.rate = 1.05;
    u.volume = 1;
    u.lang = 'en-US';
    const voice = englishVoiceRef.current || pickEnglishVoice();
    if (voice) u.voice = voice;
    window.speechSynthesis.speak(u);
  }, [pickEnglishVoice]);

  // Chrome bug workaround: speechSynthesis silently stops after ~15s idle.
  // Pinging pause()/resume() every 10s keeps the queue alive.
  useEffect(() => {
    if (!isRunning || !voiceEnabled) return;
    speechKeepAliveRef.current = setInterval(() => {
      try {
        const synth = window.speechSynthesis;
        if (synth && !synth.speaking) {
          synth.pause();
          synth.resume();
        }
      } catch (e) {}
    }, 10000);
    return () => {
      if (speechKeepAliveRef.current) clearInterval(speechKeepAliveRef.current);
    };
  }, [isRunning, voiceEnabled]);

  // Cleanup hype engine + beep ctx on unmount
  useEffect(() => {
    const engine = hypeRef.current;
    return () => {
      try { engine && engine.destroy(); } catch (e) {}
      try { beepCtxRef.current && beepCtxRef.current.close(); } catch (e) {}
      try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) {}
    };
  }, []);

  // Sync hype volume
  useEffect(() => {
    if (hypeRef.current) hypeRef.current.setVolume(hypeVolume);
  }, [hypeVolume]);

  // Start / stop hype based on training state
  useEffect(() => {
    const engine = hypeRef.current;
    if (!engine) return;
    if (isRunning && !isPaused && hypeVibe !== 'OFF') {
      engine.start(hypeVibe);
      engine.resume();
    } else if (isPaused) {
      engine.pause();
    } else if (!isRunning) {
      engine.stop();
    }
  }, [isRunning, isPaused, hypeVibe]);

  const playBeep = useCallback((type) => {
    try {
      const ctx = getBeepCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = type === 'work' ? 880 : 440;
      gain.gain.setValueAtTime(0.6, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
      osc.start();
      osc.stop(ctx.currentTime + 0.2);
    } catch (e) {}
  }, [getBeepCtx]);

  // Reset timer when drill changes or training stops
  useEffect(() => {
    if (currentDrill && isRunning) {
      const config = getTimerConfig(currentDrill);
      setCurrentSet(1);
      setPhase('work');
      setIsPaused(false);
      setTimeRemaining(config.type === 'Interval' ? config.workSeconds : config.totalSeconds);
      speak('Go!');
    }
  }, [currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentDrill && !isRunning) {
      const config = getTimerConfig(currentDrill);
      setCurrentSet(1);
      setPhase('ready');
      setTimeRemaining(config.type === 'Interval' ? config.workSeconds : config.totalSeconds);
    }
  }, [currentDrill, getTimerConfig, isRunning]);

  const goToNextDrill = useCallback(() => {
    if (currentIndex < totalDrills - 1) {
      setCurrentIndex((prev) => prev + 1);
      speak(randomPhrase('drillComplete'));
    } else {
      setIsRunning(false);
      setIsComplete(true);
      speak(randomPhrase('sessionComplete'));
    }
  }, [currentIndex, totalDrills, speak]);

  const handleTimerComplete = useCallback(() => {
    const config = getTimerConfig(currentDrill);
    if (config.type !== 'Interval') {
      goToNextDrill();
      return;
    }

    if (phase === 'work') {
      if (config.restSeconds > 0) {
        setPhase('rest');
        setTimeRemaining(config.restSeconds);
        playBeep('rest');
        speak(randomPhrase('rest'));
      } else if (currentSet < config.sets) {
        setCurrentSet((s) => s + 1);
        setTimeRemaining(config.workSeconds);
        playBeep('work');
        speak(randomPhrase('setStart', { n: currentSet + 1 }));
      } else {
        goToNextDrill();
      }
    } else if (phase === 'rest') {
      if (currentSet < config.sets) {
        setCurrentSet((s) => s + 1);
        setPhase('work');
        setTimeRemaining(config.workSeconds);
        playBeep('work');
        speak(randomPhrase('setStart', { n: currentSet + 1 }));
      } else {
        goToNextDrill();
      }
    }
  }, [currentDrill, currentSet, phase, getTimerConfig, goToNextDrill, playBeep, speak]);

  // Reset coaching flags whenever the phase / drill / set changes
  useEffect(() => {
    midWorkPhraseFiredRef.current = false;
    finalStretchFiredRef.current = false;
  }, [currentIndex, currentSet, phase]);

  useEffect(() => {
    if (isRunning && !isPaused && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setTimeout(handleTimerComplete, 100);
            return 0;
          }
          if (prev === 4) speak('Three', true);
          else if (prev === 3) speak('Two', true);
          else if (prev === 2) speak('One', true);
          return prev - 1;
        });

        // Coach phrases during work phase (longer drills only)
        if (phase === 'work') {
          const config = getTimerConfig(currentDrill);
          const total = config.type === 'Interval' ? config.workSeconds : config.totalSeconds;
          if (total >= 30) {
            // Mid-work pump (roughly halfway)
            if (!midWorkPhraseFiredRef.current && timeRemaining <= Math.floor(total / 2) && timeRemaining > 12) {
              midWorkPhraseFiredRef.current = true;
              speak(randomPhrase('midWork'));
            }
            // Final stretch (~10s left, before the 3-2-1 countdown)
            if (!finalStretchFiredRef.current && timeRemaining === 11) {
              finalStretchFiredRef.current = true;
              speak(randomPhrase('finalStretch'));
            }
          }
        }
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRunning, isPaused, timeRemaining, handleTimerComplete, speak, phase, currentDrill, getTimerConfig]);

  const formatTime = (s) =>
    `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  const handleStart = () => {
    const config = getTimerConfig(currentDrill);
    setIsRunning(true);
    setIsPaused(false);
    setPhase('work');
    setCurrentSet(1);
    setTimeRemaining(config.type === 'Interval' ? config.workSeconds : config.totalSeconds);
    speak(randomPhrase('start'));
    playBeep('work');
  };

  const handlePauseResume = () => {
    setIsPaused(!isPaused);
    speak(isPaused ? 'Go!' : 'Paused');
  };

  const handleRestart = () => {
    const config = getTimerConfig(currentDrill);
    setCurrentSet(1);
    setPhase('work');
    setTimeRemaining(config.type === 'Interval' ? config.workSeconds : config.totalSeconds);
    speak('Restart. Go!');
  };

  const handleNext = () => goToNextDrill();
  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      speak('Previous');
    }
  };
  const handleComplete = () => {
    setIsRunning(false);
    setIsComplete(true);
    speak(randomPhrase('sessionComplete'));
  };
  const handleExit = () => {
    try { hypeRef.current && hypeRef.current.stop(); } catch (e) {}
    navigate('/dashboard/train-at-home');
  };

  const timerConfig = getTimerConfig(currentDrill);
  const getPhaseInfo = () => {
    if (timerConfig.type !== 'Interval') {
      return { label: 'CONTINUOUS', color: 'bg-primary/20 text-primary' };
    }
    if (phase === 'work') return { label: 'WORK', color: 'bg-emerald-500/20 text-emerald-400' };
    if (phase === 'rest') return { label: 'REST', color: 'bg-amber-500/20 text-amber-400' };
    return { label: 'READY', color: 'bg-muted text-muted-foreground' };
  };
  const phaseInfo = getPhaseInfo();

  // No session
  if (!sessionData || drills.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-heading mb-4">No Session Found</h1>
          <Button onClick={handleExit} data-testid="guided-no-session-back">
            <ChevronLeft className="mr-2 h-4 w-4" />Back
          </Button>
        </div>
      </div>
    );
  }

  // Complete
  if (isComplete) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="h-12 w-12 text-emerald-400" />
          </div>
          <h1 className="text-4xl font-heading text-emerald-400 mb-4">Session Complete</h1>
          <p className="text-muted-foreground mb-8">
            {totalDrills} drills • {sessionData.total_duration} min
          </p>
          <Button
            onClick={handleExit}
            size="lg"
            className="bg-primary text-black"
            data-testid="guided-finish-btn"
          >
            Finish
          </Button>
        </div>
      </div>
    );
  }

  // Preview (not running)
  if (!isRunning) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-lg w-full">
          <Button
            variant="ghost"
            onClick={handleExit}
            className="mb-6"
            data-testid="guided-back-btn"
          >
            <ChevronLeft className="mr-2 h-4 w-4" />Back
          </Button>

          <div className="bg-card border border-border rounded-lg p-6 text-center">
            <div className="text-xs uppercase tracking-wider text-primary mb-2">
              Generated from your Coach's Methodology
            </div>
            <h1 className="text-2xl font-heading mb-4">{sessionData.session_title}</h1>

            <div className="flex justify-center gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-primary">{sessionData.total_duration}</div>
                <div className="text-xs text-muted-foreground">MINUTES</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-primary">{totalDrills}</div>
                <div className="text-xs text-muted-foreground">DRILLS</div>
              </div>
            </div>

            <div className="bg-muted/30 rounded-sm p-4 mb-6 text-left">
              <h3 className="text-xs uppercase text-muted-foreground mb-3 text-center">Training Order</h3>
              {drills.slice(0, 5).map((d, i) => {
                const c = getTimerConfig(d);
                return (
                  <div key={d.id} className="flex justify-between text-sm py-1">
                    <span className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center">
                        {i + 1}
                      </span>
                      {d.name}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {c.type === 'Interval' ? `${c.sets}x${c.workSeconds}s` : `${Math.ceil(c.totalSeconds / 60)}min`}
                    </span>
                  </div>
                );
              })}
              {drills.length > 5 && (
                <div className="text-xs text-muted-foreground text-center mt-2">
                  +{drills.length - 5} more
                </div>
              )}
            </div>

            <div className="flex justify-center gap-3 mb-4">
              <button
                onClick={() => setVoiceEnabled(!voiceEnabled)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                  voiceEnabled ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                }`}
                data-testid="guided-voice-toggle"
              >
                {voiceEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
                Coach {voiceEnabled ? 'On' : 'Off'}
              </button>
              <button
                onClick={testCoach}
                className="flex items-center gap-2 px-3 py-1.5 rounded text-sm bg-muted text-muted-foreground hover:text-primary hover:bg-primary/10"
                data-testid="guided-coach-test"
              >
                Test Coach
              </button>
            </div>

            {/* HYPE MODE — vibe selector + volume */}
            <div className="bg-black/40 border border-border/40 rounded-md p-4 mb-4 text-left" data-testid="hype-mode-panel">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-widest text-primary flex items-center gap-2">
                  <Music size={14} /> Hype Mode
                </span>
                <span className="text-[10px] uppercase text-muted-foreground">
                  Procedural beats
                </span>
              </div>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {['OFF', ...Object.keys(PRESETS)].map((key) => {
                  const preset = PRESETS[key];
                  const active = hypeVibe === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setHypeVibe(key)}
                      data-testid={`hype-vibe-${key.toLowerCase()}`}
                      className={`px-2 py-2 rounded text-xs font-bold uppercase tracking-wider transition border ${
                        active
                          ? 'bg-primary text-black border-primary'
                          : 'bg-transparent text-muted-foreground border-border/40 hover:border-primary/40'
                      }`}
                      style={active && preset ? { backgroundColor: preset.color, borderColor: preset.color, color: '#000' } : undefined}
                    >
                      {key === 'OFF' ? 'OFF' : preset.label}
                      {preset && (
                        <div className="text-[9px] font-normal opacity-80 mt-0.5">
                          {preset.bpm} BPM
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              {hypeVibe !== 'OFF' && (
                <div className="flex items-center gap-2">
                  <Music2 size={14} className="text-muted-foreground" />
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={hypeVolume}
                    onChange={(e) => setHypeVolume(parseFloat(e.target.value))}
                    className="flex-1 accent-primary"
                    data-testid="hype-volume-slider"
                  />
                  <span className="text-xs text-muted-foreground w-8 text-right">
                    {Math.round(hypeVolume * 100)}
                  </span>
                </div>
              )}
              {hypeVibe !== 'OFF' && PRESETS[hypeVibe] && (
                <p className="text-[10px] text-muted-foreground mt-2 italic">
                  {PRESETS[hypeVibe].description}
                </p>
              )}
            </div>

            <Button
              onClick={handleStart}
              size="lg"
              className="bg-primary text-black w-full"
              data-testid="guided-start-btn"
            >
              <Play className="mr-2 h-5 w-5" />Start Training
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Active training
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="bg-muted/30 h-2">
        <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
      </div>

      <div className="p-3 border-b border-border/30 flex justify-between items-center">
        <span className="text-xs uppercase text-muted-foreground">
          Drill {currentIndex + 1}/{totalDrills}
        </span>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setHypeVibe(hypeVibe === 'OFF' ? 'HYPE' : 'OFF')}
            className={hypeVibe !== 'OFF' ? 'text-primary' : 'text-muted-foreground'}
            data-testid="hype-mode-quick-toggle"
            title={hypeVibe === 'OFF' ? 'Hype Mode off' : `Hype: ${hypeVibe}`}
          >
            {hypeVibe !== 'OFF' ? <Music size={16} /> : <Music2 size={16} />}
          </button>
          <button
            onClick={() => setVoiceEnabled(!voiceEnabled)}
            className={voiceEnabled ? 'text-primary' : 'text-muted-foreground'}
            data-testid="guided-voice-toggle-active"
          >
            {voiceEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
          {timerConfig.type === 'Interval' && (
            <span className="text-xs text-primary ml-2">
              Set {currentSet}/{timerConfig.sets}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="text-xs uppercase text-primary bg-primary/10 px-3 py-1 rounded mb-3">
            {currentDrill.category || 'Training'}
          </div>
          <h1 className="font-heading mb-3 text-3xl">{currentDrill.name}</h1>
          <div className={`uppercase tracking-wider mb-3 px-4 py-1.5 rounded text-xl ${phaseInfo.color}`}>
            {phaseInfo.label}
          </div>
          <div
            className={`font-mono font-bold mb-4 text-7xl ${
              timeRemaining <= 5
                ? 'text-red-400 animate-pulse'
                : timeRemaining <= 10
                ? 'text-amber-400'
                : phase === 'work'
                ? 'text-primary'
                : 'text-blue-400'
            }`}
          >
            {formatTime(timeRemaining)}
          </div>
          {timerConfig.type === 'Interval' && (
            <div className="flex gap-2 mb-3">
              {[...Array(timerConfig.sets)].map((_, i) => (
                <div
                  key={i}
                  className={`w-3 h-3 rounded-full ${
                    i < currentSet - 1
                      ? 'bg-emerald-400'
                      : i === currentSet - 1
                      ? phase === 'work'
                        ? 'bg-primary animate-pulse'
                        : 'bg-blue-400'
                      : 'bg-muted'
                  }`}
                />
              ))}
            </div>
          )}
          {isPaused && <div className="text-xl text-amber-400 animate-pulse mb-3">PAUSED</div>}
          <p className="text-muted-foreground max-w-xl mb-4">{currentDrill.description}</p>
        </div>
      </div>

      <div className="p-4 border-t border-border/30">
        <div className="flex justify-center gap-3 max-w-lg mx-auto">
          <Button
            variant="outline"
            size="lg"
            onClick={handleBack}
            disabled={currentIndex === 0}
            className="w-12 h-12 rounded-full"
            data-testid="guided-prev-btn"
          >
            <SkipBack className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={handleRestart}
            className="w-12 h-12 rounded-full"
            data-testid="guided-restart-btn"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
          <Button
            size="lg"
            onClick={handlePauseResume}
            className={`w-16 h-16 rounded-full ${
              isPaused ? 'bg-emerald-500' : 'bg-amber-500'
            } text-black`}
            data-testid="guided-pause-btn"
          >
            {isPaused ? <Play className="h-6 w-6" /> : <Pause className="h-6 w-6" />}
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={handleNext}
            className="w-12 h-12 rounded-full"
            data-testid="guided-next-btn"
          >
            <SkipForward className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex justify-center mt-3">
          <Button
            variant="ghost"
            onClick={handleComplete}
            className="text-muted-foreground text-sm"
            data-testid="guided-complete-btn"
          >
            <Check className="mr-2 h-4 w-4" />Complete
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GuidedTraining;
